import { GET_ENUMS, GET_PARAMS } from 'const';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'ui-kit/hooks';
import { FEBottomSheet, FEDialog, FENotificationCard, FESpinner } from 'components';
import {
  $constructors,
  $dishes,
  $cutleries,
  $isValidating,
  $price,
  $problems,
  $promoCode,
  $promotion,
  $promotionProduct,
  $timeTo,
  $urgent,
  $warning,
  orderingForm,
  cartGate,
  $createdOrderNotificationStore,
  closeCreatedOrderModal,
  changePromotionProduct,
  clearCartAfterOrder,
  $hasCriticalCartError,
  $validationErrors,
  hideValidationError,
  validate,
  $longDeliveryWarning,
  CreateOrderFxModel,
  createOrderFx,
  $avaliableOrderPaymentTypes,
  clearCart,
} from 'domains/cart';
import {
  $type,
  $timeIntervals,
  $deliveryZone,
  $address,
  $restaurant,
  $currency,
  $country,
  $countriesList,
  CountryCodeModel,
  $city,
} from 'domains/cartography';
import {
  setTime,
  deleteConstructor,
  deleteCutlery,
  deleteDish,
  changeConstructorQuantity,
  changeCutleryQuantity,
  changeDishQuantity,
  setPromocodeFx,
} from 'domains/cart';
import { $bonusAccount, $user } from 'domains/profile';
import {
  $retryPaymentOrder,
  $retryPaymentTypes,
  $isRetryPaymentPending,
  closeRetryPayment,
  retryOrderPaymentFx,
  openRetryPayment,
  getOrderByIdFx,
} from 'domains/profile';
import { useGate, useUnit } from 'effector-react';
import { Content } from './components';
import { useField } from 'effector-forms';
import { OrderType, PaymentType } from 'models';
import { useGetParameter, usePrepareLink } from 'hooks/router';
import {
  mapConstructorsFromCartToValidate,
  mapDishesFromCartToValidate,
} from 'domains/cart/model';
import { $features } from 'domains/features';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { $catalogCategories, CatalogCategory } from 'domains/catalog';

const CartPage = ({ open }: { open: boolean }) => {
  const { t } = useTranslation();
  const [stage, setStage] = React.useState<'CART' | 'ORDERING'>('CART');
  const isDesktop = useMediaQuery(`(min-width: ${600}px)`);
  const navigate = useNavigate();

  useGate(cartGate);

  const retryPaymentOrder = useUnit($retryPaymentOrder);
  const retryPaymentTypes = useUnit($retryPaymentTypes);
  const isRetryPaymentPending = useUnit($isRetryPaymentPending);
  const onCloseRetryPayment = useUnit(closeRetryPayment);
  const onRetryOrderPayment = useUnit(retryOrderPaymentFx);
  const onOpenRetryPayment = useUnit(openRetryPayment);

  const retryOrderIdFromQuery = useGetParameter(GET_PARAMS.retryOrderId);
  const isRetryPaymentMode =
    retryPaymentOrder !== null || Boolean(retryOrderIdFromQuery);
  const isRetryPaymentLoading =
    open && Boolean(retryOrderIdFromQuery) && retryPaymentOrder === null;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!retryOrderIdFromQuery) {
      if (retryPaymentOrder) {
        onCloseRetryPayment();
      }
      setStage('CART');
      return;
    }

    if (!retryPaymentOrder) {
      getOrderByIdFx({ id: retryOrderIdFromQuery }).then((order) => {
        onOpenRetryPayment({ order });
      });
      return;
    }

    setStage('ORDERING');
  }, [
    open,
    retryOrderIdFromQuery,
    retryPaymentOrder,
    onOpenRetryPayment,
    onCloseRetryPayment,
  ]);

  const isRetryPayment = retryPaymentOrder !== null;

  const title = React.useMemo(
    () =>
      isRetryPaymentMode
        ? t('orders.retryPayment.title')
        : stage === 'CART'
          ? t('cart.cartTitle')
          : t('cart.orderingTitle'),
    [stage, t, isRetryPaymentMode],
  );

  const currency = useUnit($currency);

  const [
    dishes,
    onChangeDishQuantity,
    onDeleteDish,
    constructors,
    onChangeConstructorQuantity,
    onDeleteConstructor,
    cutleries,
    onChangeCutleryQuantity,
    onDeleteCutlery,
    categories,
  ] = useUnit([
    $dishes,
    changeDishQuantity,
    deleteDish,
    $constructors,
    changeConstructorQuantity,
    deleteConstructor,
    $cutleries,
    changeCutleryQuantity,
    deleteCutlery,
    $catalogCategories,
  ]);

  const [orderModalState, closeOrderModal, onClearCartAfterOrder] = useUnit([
    $createdOrderNotificationStore,
    closeCreatedOrderModal,
    clearCartAfterOrder,
  ]);

  const onCreateOrder = useUnit(createOrderFx);

  const paymentTypes = useUnit($avaliableOrderPaymentTypes);
  const user = useUnit($user);
  const bonusAccount = useUnit($bonusAccount);
  const features = useUnit($features);

  const [timeTo, urgent, onChangeTime] = useUnit([$timeTo, $urgent, setTime]);

  const [promoCode] = useUnit([$promoCode]);
  const onChagePromocode = useUnit(setPromocodeFx);

  const [country, countries] = useUnit([$country, $countriesList]);

  const [countryCode, setCurrentCountryCode] =
    useState<CountryCodeModel | null>(null);

  useEffect(() => {
    setCurrentCountryCode(country?.code || null);
  }, [country]);

  const [promotion, promotionProduct, onChangePromotionProduct] = useUnit([
    $promotion,
    $promotionProduct,
    changePromotionProduct,
  ]);

  const [type, timeIntervals, deliveryZone, address, restaurant, city] =
    useUnit([
      $type,
      $timeIntervals,
      $deliveryZone,
      $address,
      $restaurant,
      $city,
    ]);

  const [
    hasCriticalCartError,
    validationErrors,
    onHideValidationError,
    onValidate,
    onClearCart,
  ] = useUnit([
    $hasCriticalCartError,
    $validationErrors,
    hideValidationError,
    validate,
    clearCart,
  ]);

  const customer = useUnit($user);

  const isValidating = useUnit($isValidating);

  const [price, warning, longDeliveryWarning, problems] = useUnit([
    $price,
    $warning,
    $longDeliveryWarning,
    $problems,
  ]);

  const { value: phone, onChange: onChangePhone } = useField(
    orderingForm.fields.phone,
  );
  const { value: name, onChange: onChangeName } = useField(
    orderingForm.fields.name,
  );
  const { value: comment, onChange: onChangeComment } = useField(
    orderingForm.fields.comment,
  );
  const { value: paymentCardId, onChange: onChangePaymentCardId } = useField(
    orderingForm.fields.paymentCardId,
  );
  const { value: paymentType, onChange: onChangePaymentType } = useField(
    orderingForm.fields.paymentType,
  );
  const { value: bonusAmount, onChange: onChangeBonusAmount } = useField(
    orderingForm.fields.bonusAmount,
  );
  const { value: changeFrom, onChange: onChangeChangeFrom } = useField(
    orderingForm.fields.changeFrom,
  );

  const createOrderData: CreateOrderFxModel & {
    categories: CatalogCategory[];
    paymentCardId: string | null;
  } = {
    paymentCardId,
    categories,
    dishes: mapDishesFromCartToValidate(dishes),
    constructors: mapConstructorsFromCartToValidate(constructors),
    isLogin: customer !== null,
    name: name || '',
    phone: phone || '',
    timeTo,
    urgent,
    cutleries: cutleries.map((cutlery) => ({
      orderItemId: cutlery.orderItemId,
      complectationId: cutlery.complectation.complectation.id,
      quantity: cutlery.quantity,
    })),
    promoCode,
    type,
    receivingAddress: address,
    receivingRestaurantId: restaurant ? restaurant.id : null,
    promotionProduct: promotionProduct
      ? promotionProduct.product.product.id
      : null,
    customerCount: 1,
    paymentType: paymentType!,
    bonusAmount,
    comment,
    changeFrom,
  };

  const emptyCartDescription = React.useMemo(() => {
    if (type === 'COURIER')
      return `${t('cart.emptyCart.description')}
    ${t('cart.emptyCart.caption', {
      deliveryPrice: deliveryZone?.conditions?.deliveryPriceConditions
        ? Math.min(
            ...deliveryZone.conditions.deliveryPriceConditions.map(
              (condition) => condition.orderTotalMinimum,
            ),
          )
        : 0,
      currency,
    })}`;
    return t('cart.emptyCart.description');
  }, [type, deliveryZone, t, currency]);

  const cartIsEmpty = React.useMemo(
    () => dishes.length === 0 && constructors.length === 0,
    [dishes, constructors],
  );

  const showEmptyCart = cartIsEmpty && !isRetryPaymentMode;

  const retryPrice = retryPaymentOrder
    ? {
        cart: retryPaymentOrder.price.cart,
        total: retryPaymentOrder.price.total,
        discount: retryPaymentOrder.price.discount,
        delivery: retryPaymentOrder.price.delivery,
      }
    : null;
  const [orderType, setOrderType] = React.useState<OrderType>('COURIER');

  const displayPrice = isRetryPayment ? retryPrice : price;
  const displayPaymentTypes = (
    isRetryPayment && retryPaymentTypes.length > 0
      ? retryPaymentTypes
      : paymentTypes
  ) as Exclude<PaymentType, 'BONUS' | 'SBERPAY'>[];
  const displayOrderType = isRetryPayment
    ? retryPaymentOrder!.type
    : orderType;
  const displayIsValidating = isRetryPayment
    ? isRetryPaymentPending
    : isValidating;
  const displayStage = isRetryPaymentMode ? ('ORDERING' as const) : stage;


  const [availableDeliveryTypes, setAvailableDeliveryTypes] = useState<
    OrderType[]
  >(['COURIER', 'GO_TO_PLACE']);

  React.useEffect(() => {
    if (restaurant) {
      setAvailableDeliveryTypes([
        'COURIER',
        ...restaurant.availableDeliveryTypes,
      ]);
    } else {
      setAvailableDeliveryTypes(['COURIER', 'GO_TO_PLACE']);
    }
  }, [restaurant]);

  React.useEffect(() => {
    setOrderType((currentType) => {
      if (restaurant && type === 'GO_TO_PLACE') {
        if (restaurant.availableDeliveryTypes.includes(type)) {
          if (
            restaurant.availableDeliveryTypes.includes(
              currentType as 'GO_TO_PLACE' | 'IN_PLACE',
            )
          ) {
            return currentType;
          }
          return type;
        }
        return restaurant.availableDeliveryTypes[0];
      }
      return type;
    });
  }, [type, restaurant]);

  const orderLink = usePrepareLink({
    pushToQuery: {
      [GET_PARAMS.orderId]: orderModalState.orderId || '',
    },
    query: {
      [GET_PARAMS.page]: GET_ENUMS.page.orders,
    },
  });

  const retryOrderLink = usePrepareLink({
    pushToQuery: {
      [GET_PARAMS.orderId]: retryPaymentOrder?.id || '',
    },
    query: {
      [GET_PARAMS.page]: GET_ENUMS.page.orders,
    },
  });

  const handleRetryPaymentBack = () => {
    onCloseRetryPayment();
    navigate(retryOrderLink, { replace: true });
  };

  const handleRetryPayment = (params: {
    orderId: string;
    amount: number;
    paymentType: Exclude<PaymentType, 'BONUS' | 'SBERPAY'>;
    paymentCardId: string | null;
  }) =>
    onRetryOrderPayment(params).then((result: any) => {
      navigate(retryOrderLink, { replace: true });
      return result;
    });

  const cartographyLink = usePrepareLink({
    query: {
      [GET_PARAMS.page]: GET_ENUMS.page.cartography,
    },
    pushToQuery: {
      [GET_PARAMS.page]: GET_ENUMS.page.cart,
    },
    keepOldQuery: true,
  });

  const contentProps = {
    onChangePaymentCardId,
    paymentCardId,
    availableDeliveryTypes,
    categories,
    changeCountryCode: setCurrentCountryCode,
    countries: countries.map((country) => country.code),
    country: countryCode,
    onClearCart,
    currency,
    features,
    createOrderData,
    isDesktop,
    title,
    onArrowBackClick: isRetryPaymentMode
      ? handleRetryPaymentBack
      : () => setStage('CART'),
    hasCriticalCartError,
    cartographyLink,
    dishes,
    constructors,
    cutleries,
    price: displayPrice,
    warning,
    longDeliveryWarning,
    isValidating: displayIsValidating,
    timeTo,
    urgent,
    promoCode,
    onChangePromoCode: (promoCode: string | null) =>
      onChagePromocode({
        promoCode,
        validationPayload: {
          deliveryZoneId: deliveryZone?.id || null,
          request: {
            dishes,
            cutleries: cutleries.map((cutlery) => ({
              orderItemId: cutlery.orderItemId,
              complectationId: cutlery.complectation.complectation.id,
              quantity: cutlery.quantity,
            })),
            constructors,
            promotionProduct: promotionProduct?.product.product.id || null,
            urgent,
            timeTo,
            type,
            receivingAddress: address,
            receivingRestaurantId: restaurant ? restaurant.id : null,
            customerCount: 1,
            customer: customer
              ? {
                  name: customer?.fullName?.firstName || '',
                  phone: customer?.phone || '',
                }
              : null,
          },
        },
      }),
    onChangeTime,
    stage: displayStage,
    promotion,
    promotionProduct,
    problems,
    deliveryZone,
    type: displayOrderType,
    timeIntervals,
    address,
    restaurant,
    onChangeStage: (nextStage: 'CART' | 'ORDERING') => setStage(nextStage),
    name,
    onChangeName,
    phone,
    onChangePhone,
    comment,
    onChangeComment,
    paymentType,
    onChangePaymentType,
    bonusAmount,
    onChangeBonusAmount,
    changeFrom,
    onChangeChangeFrom,
    onChangeConstructorQuantity,
    onChangeCutleryQuantity,
    onChangeDishQuantity,
    onDeleteConstructor,
    onDeleteCutlery,
    onDeleteDish,
    paymentTypes: displayPaymentTypes,
    user,
    bonusAccount,
    onCreateOrder,
    navigateFunction: navigate,
    onChangePromotionProduct,
    onChangeOrderType: setOrderType,
    isRetryPayment,
    retryOrderId: retryPaymentOrder?.id ?? retryOrderIdFromQuery ?? undefined,
    onRetryPayment: handleRetryPayment,
  };

  const handleCartClose = () => {
    if (isRetryPaymentMode) {
      handleRetryPaymentBack();
      return;
    }

    navigate(-1);
  };

  if (isRetryPaymentLoading) {
    return <FESpinner />;
  }

  return (
    <>
      {city && (
        <Helmet>
          <title>{t('meta.cart.title', { city: city.name })}</title>
          <meta
            name='description'
            content={t('meta.cart.metaDescription', { city: city.name })}
          />
        </Helmet>
      )}
      {showEmptyCart ? (
        <FENotificationCard
          icon='shoppingCart'
          title={t('cart.emptyCart.title')}
          open={showEmptyCart}
          onClose={handleCartClose}
          description={emptyCartDescription}
          primaryButtonProps={{
            text: t('cart.emptyCart.buttonText'),
            closeOnClick: true,
            onClick: () => {},
          }}
        />
      ) : isDesktop ? (
        <FEDialog
          open={open}
          onClose={handleCartClose}
          render={({ onClose }) => (
            <Content
              {...contentProps}
              onClose={isRetryPaymentMode ? handleRetryPaymentBack : onClose}
            />
          )}
        />
      ) : (
        <FEBottomSheet
          open={open}
          onClose={handleCartClose}
          render={({ onClose }) => (
            <Content
              {...contentProps}
              onClose={isRetryPaymentMode ? handleRetryPaymentBack : onClose}
            />
          )}
        />
      )}
      {validationErrors.map((error, i) => (
        <FENotificationCard
          open={true}
          key={i}
          title={error.title}
          description={error.message}
          icon={'sorry'}
          onClose={() => {
            onHideValidationError({ index: i });
            switch (error.actionType) {
              case 'cartography':
                navigate(cartographyLink);
                break;
              // case 'catalog':
              //   navigate('/', { replace: true });
              //   break;
              case 'hide':
                break;
              case 'revalidate':
                onValidate();
                break;
            }
          }}
          primaryButtonProps={{
            closeOnClick: true,
            text: t(error.buttonText),
            onClick: () => {},
          }}
        />
      ))}
      <FENotificationCard
        open={orderModalState.show}
        icon={orderModalState.icon || 'checkmark'}
        title={orderModalState.title || ''}
        description={orderModalState.description || ''}
        onClose={() => {
          closeOrderModal();
          navigate('/');
          onClearCartAfterOrder();
        }}
        primaryButtonProps={{
          closeOnClick: true,
          text: orderModalState.button?.text || '',
          onClick: () => {
            orderModalState.type === 'SUCCESS' && onClearCartAfterOrder();
            navigate(orderLink, { replace: true });
          },
        }}
      />
    </>
  );
};

export default CartPage;
